'use client';

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  createTradesmanUpdate,
  getTradesmanUpdates,
  deleteTradesmanUpdate
} from '@/app/actions/tradesman-updates';
import { toast } from 'sonner';

interface Update {
  id: string;
  content: string;
  createdAt: Date | string;
}

interface DailyUpdatesFeedProps {
  adId: string;
  isOwner?: boolean;
}

export function DailyUpdatesFeed({ adId, isOwner = false }: DailyUpdatesFeedProps) {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newUpdate, setNewUpdate] = useState('');

  useEffect(() => {
    loadUpdates();
  }, [adId]);

  async function loadUpdates() {
    const result = await getTradesmanUpdates(adId);
    if (result.success && result.data) {
      setUpdates(result.data as Update[]);
    }
    setLoading(false);
  }

  async function handlePost() {
    if (!newUpdate.trim()) return;

    setPosting(true);
    const result = await createTradesmanUpdate(adId, newUpdate);

    if (result.success) {
      toast.success('Update posted!');
      setNewUpdate('');
      setShowForm(false);
      loadUpdates();
    } else {
      toast.error(result.error || 'Failed to post update');
    }
    setPosting(false);
  }

  async function handleDelete(updateId: string) {
    if (!confirm('Delete this update?')) return;

    const result = await deleteTradesmanUpdate(updateId);
    if (result.success) {
      toast.success('Update deleted');
      loadUpdates();
    } else {
      toast.error('Failed to delete update');
    }
  }

  const wordCount = newUpdate.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg text-slate-900">Daily Updates</h3>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="bg-card hover:bg-slate-50"
          >
            <Plus className="h-4 w-4 mr-1" />
            Post Update
          </Button>
        )}
      </div>

      {/* Post Form */}
      {showForm && isOwner && (
        <Card className="p-4 bg-card border-slate-200">
          <Textarea
            value={newUpdate}
            onChange={(e) => setNewUpdate(e.target.value)}
            placeholder="What's new with your business? (max 200 words)"
            rows={4}
            className="mb-2"
          />
          <div className="flex justify-between items-center">
            <span className={`text-sm font-medium ${wordCount > 200 ? 'text-red-500' : 'text-slate-600'}`}>
              {wordCount} / 200 words
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setNewUpdate('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handlePost}
                disabled={posting || wordCount === 0 || wordCount > 200}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {posting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Post
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Updates List */}
      {loading ? (
        <div className="text-center py-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
        </div>
      ) : updates.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          No updates yet
        </div>
      ) : (
        <div className="space-y-3">
          {updates.map((update) => (
            <Card key={update.id} className="p-4 bg-card border-slate-200">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-slate-500 font-medium">
                  {format(new Date(update.createdAt), 'MMM d, yyyy')}
                </span>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(update.id)}
                    className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{update.content}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
